import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import { resolveContentDispositionFilename } from 'apis/exclusive-deals/resolve-content-disposition-filename';
import type { AxiosError } from 'axios';
import fileDownload from 'js-file-download';
import { UseQueryOptions, UseQueryResult } from 'react-query';
import { masterProductFamiliesEndpoint } from './use-get-master-product-families-api';

const endpoint = `${masterProductFamiliesEndpoint}/sheet/download`;
export const downloadMasterProductFamiliesSheetEndpointIdentifier = endpoint;

type TData = void;
type TError = AxiosError;
type TQueryKey = [string];
type Options = Omit<UseQueryOptions<void, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

/**
 * Download the master product families Excel sheet (also the upload template):
 * Name + Category columns, one row per family. Disabled by default — trigger via refetch().
 */
export function useDownloadMasterProductFamiliesSheetApi(options?: Options): ReturnType {
  const toast = useCustomToast();

  const queryFn = async () => {
    try {
      const response = await http.get(endpoint, { responseType: 'arraybuffer' });
      const today = new Date().toISOString().split('T')[0];
      const filename = resolveContentDispositionFilename(
        response.headers?.['content-disposition'],
        `master-product-families-${today}.xlsx`,
      );
      fileDownload(response.data, filename);
    } catch (error: any) {
      if (!error.response?.status || error.response?.status === 401) throw error;
      toast({ description: 'Failed to download the master product families sheet', status: 'error' });
      throw error;
    }
  };

  return useQueryWrapper([endpoint], queryFn, { enabled: false, ...options });
}
